import React, { useEffect, useState } from 'react';
import { Loader2, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const GROUP_LABEL = { confidence: 'Observed Confidence', application: 'Application Health', behavior: 'Behavior Health', expected: 'Expected Confidence' };
const THRESHOLDS = ['health_green', 'health_amber', 'evidence_high', 'evidence_medium', 'alignment_tolerance'];

// Framework parameters (weights + thresholds) — configuration, not doctrine.
export default function TransferWeightsDialog({ open, onOpenChange, organizationId, canGlobal, onSaved }) {
  const [data, setData] = useState(null);
  const [w, setW] = useState({});
  const [scope, setScope] = useState('org');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const q = organizationId && organizationId !== '_all' ? `?organization_id=${encodeURIComponent(organizationId)}` : '';
    api.get(`/transfer-health/weights${q}`).then((r) => { setData(r.data); setW(r.data.weights); }).catch(() => toast.error('Could not load weights'));
  }, [open, organizationId]);

  const sum = (keys) => keys.reduce((s, k) => s + (Number(w[k]) || 0), 0);
  const groupsOk = data ? Object.values(data.groups).every((keys) => Math.abs(sum(keys) - 1) < 0.001) : false;

  const save = async (reset = false) => {
    setSaving(true);
    try {
      const body = { scope, reset, weights: w };
      if (scope === 'org' && organizationId && organizationId !== '_all') body.organization_id = organizationId;
      const r = await api.put('/transfer-health/weights', body);
      toast.success(reset ? 'Weights reset to framework defaults' : `Weights saved (${scope})`);
      setW(r.data.weights);
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="transfer-weights-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><SlidersHorizontal className="h-4 w-4" /> Transfer Health parameters</DialogTitle>
          <DialogDescription className="text-xs">Each Health group must sum to 1.00. Currently using <strong>{data?.source?.replace('_', ' ')}</strong>.</DialogDescription>
        </DialogHeader>
        {!data ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : (
          <div className="space-y-4 text-xs">
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
              {Object.entries(data.groups).map(([g, keys]) => {
                const s = sum(keys);
                return (
                  <div key={g} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`weights-group-${g}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-slate-800">{GROUP_LABEL[g]}</p>
                      <span className={`tabular-nums font-bold ${Math.abs(s - 1) < 0.001 ? 'text-emerald-600' : 'text-rose-600'}`} data-testid={`weights-sum-${g}`}>Σ {s.toFixed(2)}</span>
                    </div>
                    {keys.map((k) => (
                      <label key={k} className="block">
                        <span className="text-slate-600">{data.meta[k].label.split('·')[1]?.trim() || data.meta[k].label}</span>
                        <input type="number" step="0.05" min="0" max="1" value={w[k]} onChange={(e) => setW({ ...w, [k]: Number(e.target.value) })}
                          className="mt-0.5 w-full h-8 rounded-md border border-slate-300 px-2 tabular-nums" data-testid={`weight-${k}`} />
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {THRESHOLDS.map((k) => (
                <label key={k} className="block">
                  <span className="text-slate-600">{data.meta[k].label}</span>
                  <input type="number" step="1" min="0" max="100" value={w[k]} onChange={(e) => setW({ ...w, [k]: Number(e.target.value) })}
                    className="mt-0.5 w-full h-8 rounded-md border border-slate-300 px-2 tabular-nums" data-testid={`weight-${k}`} />
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
              <div className="flex items-center gap-2">
                <span className="text-slate-500">Save as</span>
                <select value={scope} onChange={(e) => setScope(e.target.value)} className="h-8 rounded-md border border-slate-300 px-2" data-testid="weights-scope">
                  <option value="org">This organisation</option>
                  {canGlobal && <option value="global">Global default</option>}
                </select>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => save(true)} disabled={saving} data-testid="weights-reset-btn"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset to framework</Button>
                <Button size="sm" onClick={() => save(false)} disabled={saving || !groupsOk} className="bg-teal-600 hover:bg-teal-700" data-testid="weights-save-btn">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Save weights</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
